import {
	useCallback,
	useEffect,
	useRef,
	useState,
	type CSSProperties,
	type DragEvent as ReactDragEvent,
	type KeyboardEvent as ReactKeyboardEvent,
	type WheelEvent as ReactWheelEvent,
} from "react";
import { useNavigate } from "react-router";
import { ChatView } from "@/components/chat/ChatView";
import { IconButton } from "@/components/common/IconButton";
import { ComposerInput, type ComposerInputHandle } from "@/components/chat/ComposerInput";
import { ChatHeader } from "@/components/chat/ChatHeader";
import UserChoicePrompt from "@/components/hitl/user-choice/UserChoicePrompt";
import { ToolApprovalPrompt } from "@/components/hitl/tool-approval/ToolApprovalPrompt";
import { useAgentSessionContext } from "@/components/chat/AgentSessionContext";
import {
	keyScrollIntent,
	NEAR_BOTTOM_THRESHOLD_PX,
	nextFollowBottom,
	type ScrollIntent,
} from "@/components/layout/followBottom";
import {
	selectCanComposeMessage,
	selectIsChatEnvironmentStopped,
	selectEffectiveEnvironmentId,
	selectIsDraftEnvironmentReady,
	selectMessageRunSettings,
	useChatStore,
} from "@/stores/useChatStore";
import { refreshChatEnvironmentStatus } from "@/components/chat/useChatEnvironmentStatusSync";
import { ScheduledRestartBanner } from "@/components/chat/ScheduledRestartBanner";
import { useChatDraftStore } from "@/stores/useChatDraftStore";
import { useTranscriptSearchStore } from "@/stores/useTranscriptSearchStore";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { useToastStore } from "@/stores/useToastStore";
import {
	ChatSendConflictError,
	ChatSendValidationError,
	createBlankChat,
	generateChatTitle,
	getActiveAgentJob,
	listChatMessages,
	postChatMessage,
	type NewChatEnvironment,
} from "@/lib/api";
import {
	shouldReconnectAfterSend,
	shouldResetStatusToIdleAfterSendFailure,
} from "@/lib/composer-actions";
import { isFileDrag } from "@/lib/dnd/file-drag";
import { SendOwnershipRegistry, type SendOwnerKey } from "@/lib/send-ownership";
import {
	planChatAttachmentUploads,
	refreshEnvironmentFiles,
	uploadChatAttachments,
	type ChatAttachmentUploadPlan,
} from "@/lib/environment-files";
import { newMessageId, newRequestId } from "@/lib/id-generator";
import {
	attachmentFromUploaded,
	routeAttachments,
	type ComposerAttachment,
	type RoutedAttachments,
} from "@/lib/chat-attachments";
import { toUploadedFileBlock, uploadedFile, type UploadedFile } from "@/lib/uploaded-file-block";
import {
	nextMessageOrder,
	type ChatMessageDto,
	type ChatMetadata,
	type ContentBlock,
} from "@/lib/chat";

/** 履歴とコンポーザーを載せるチャット欄の最大幅 (px)。 */
const TRANSCRIPT_MAX_WIDTH = 768;

/** チャット欄の左右余白 (px)。 */
const TRANSCRIPT_PADDING_X = 24;

/**
 * チャット欄の最小幅 (px)。コンポーザー下段の操作列は縮まないため、これより狭いと
 * 送信ボタンが箱からはみ出す。内訳は操作列 333 (クリップ 24 + 確認モード 81 +
 * モデル 136 + コンテキスト 24 + 送信 28 + 間隔 8×5) + 箱の左右パディング 16 +
 * 左右余白 48 = 397 で、フォントのフォールバック差ぶんだけ上へ丸めた値。
 */
export const CHAT_COLUMN_MIN_WIDTH = 408;

/**
 * 履歴の下に並ぶもの (HITL カード・コンポーザー) の列。左右余白を最大幅の内側へ入れて、
 * 履歴の本文と実効幅・左右端を揃える。
 */
const CHAT_COLUMN_STYLE: CSSProperties = {
	width: "100%",
	maxWidth: TRANSCRIPT_MAX_WIDTH,
	padding: `0 ${TRANSCRIPT_PADDING_X}px`,
};

/** OS からのファイルドラッグ中に表示するドロップ受付オーバーレイのスタイル。 */
const DROP_OVERLAY_STYLE: CSSProperties = {
	position: "absolute",
	inset: 12,
	pointerEvents: "none",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	background: "var(--surface-1)",
	boxShadow: "0 0 0 2px var(--accent-solid)",
	borderRadius: "var(--radius-4)",
	fontSize: "var(--text-ui)",
	color: "var(--fg-1)",
	zIndex: 10,
};

async function generateChatTitleAndUpdate(chatId: string): Promise<void> {
	try {
		const meta = await generateChatTitle(chatId);
		if (!meta.title) return;
		const store = useChatStore.getState();
		store.updateChatListItem(chatId, { title: meta.title });
		if (store.chatId === chatId) store.setCurrentTitle(meta.title);
	} catch (err: unknown) {
		useToastStore.getState().show({
			type: "error",
			message: err instanceof Error ? err.message : "チャットタイトルの生成に失敗しました。",
		});
		// 呼び出し元の fire-and-forget 境界へ元のエラーを返し、失敗を無視しない。
		throw err;
	}
}

export function CenterPane() {
	const chatId = useChatStore((s) => s.chatId);
	const currentTitle = useChatStore((s) => s.currentTitle);

	const chatArchived = useChatStore((s) => s.chatArchived);
	const environmentDeleted = useChatStore((s) => s.chatId !== null && s.environmentId === null);
	const environmentStopped = useChatStore(selectIsChatEnvironmentStopped);
	const canComposeMessage = useChatStore(selectCanComposeMessage);
	const creditExhausted = useCurrentUserStore((s) => s.creditExhausted);
	const isDraftEnvironmentReady = useChatStore(selectIsDraftEnvironmentReady);
	const addMessage = useChatStore((s) => s.addMessage);
	const setPendingUserMessage = useChatStore((s) => s.setPendingUserMessage);
	const status = useChatStore((s) => s.status);
	const { startStreaming, stopAgentSession } = useAgentSessionContext();
	const sendOwnershipRef = useRef(new SendOwnershipRegistry());
	const [pendingSendKeys, setPendingSendKeys] = useState<ReadonlySet<SendOwnerKey>>(
		() => new Set(),
	);
	const setPendingSendKey = useCallback((key: SendOwnerKey, pending: boolean) => {
		setPendingSendKeys((current) => {
			if (current.has(key) === pending) return current;
			const next = new Set(current);
			if (pending) next.add(key);
			else next.delete(key);
			return next;
		});
	}, []);

	// 送信ボタンが実行中(streaming)に停止ボタンとして押されたときの処理。
	const handleStop = useCallback(() => {
		void stopAgentSession().catch((err: unknown) => {
			console.error("エージェントの停止に失敗しました", err);
		});
	}, [stopAgentSession]);
	const navigate = useNavigate();
	const composerRef = useRef<ComposerInputHandle>(null);

	const closeTranscriptSearch = useTranscriptSearchStore((s) => s.closeSearch);
	// 検索を閉じたら、そのまま入力を続けられるようコンポーザーへフォーカスを戻す。
	const handleCloseTranscriptSearch = useCallback(() => {
		closeTranscriptSearch();
		composerRef.current?.focus();
	}, [closeTranscriptSearch]);

	const scrollRef = useRef<HTMLDivElement>(null); // transcript-scroll
	const contentRef = useRef<HTMLDivElement>(null); // 内側の最大幅つき div (高さ変化の監視対象)
	// 最下部付近にいるか (ジャンプボタンの表示判定)。初期値 true でボタンの初期ちらつきを防ぐ。
	const [isNearBottom, setIsNearBottom] = useState(true);
	// 本文の伸長に追従して最下部へ寄せるか。ResizeObserver コールバックから読むため ref で持つ。
	const followBottomRef = useRef(true);

	// setState は同値なら React が bail out する。
	const updateIsNearBottom = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		const near = el.scrollHeight - el.scrollTop - el.clientHeight <= NEAR_BOTTOM_THRESHOLD_PX;
		setIsNearBottom(followBottomRef.current || near);
	}, []);

	const lastScrollTopRef = useRef(0);
	const scrollIntentRef = useRef<ScrollIntent>(null);
	const handleTranscriptScroll = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		const delta = el.scrollTop - lastScrollTopRef.current;
		lastScrollTopRef.current = el.scrollTop;
		followBottomRef.current = nextFollowBottom(
			followBottomRef.current,
			scrollIntentRef.current,
			delta,
			el.scrollHeight - el.scrollTop - el.clientHeight,
		);
		scrollIntentRef.current = null;
		updateIsNearBottom();
	}, [updateIsNearBottom]);

	// 上向きの操作では scroll イベントを待たずに追従を止める。待つ間に ResizeObserver が最下部へ吸着し、操作を打ち消すため。
	const noteScrollIntent = useCallback((intent: ScrollIntent) => {
		if (intent === null) return;
		scrollIntentRef.current = intent;
		if (intent === "up" && (scrollRef.current?.scrollTop ?? 0) > 0) followBottomRef.current = false;
	}, []);

	const handleTranscriptWheel = useCallback(
		(e: ReactWheelEvent) => {
			if (e.deltaY !== 0) noteScrollIntent(e.deltaY < 0 ? "up" : "down");
		},
		[noteScrollIntent],
	);
	const handleTranscriptKeyDown = useCallback(
		(e: ReactKeyboardEvent) => noteScrollIntent(keyScrollIntent(e.key, e.shiftKey)),
		[noteScrollIntent],
	);

	const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
		const el = scrollRef.current;
		if (!el) return;
		el.scrollTo({ top: el.scrollHeight, behavior });
		// smooth は目標がクリック時点の高さで固定され、伸長中は最下部の手前で止まるため、到達を待たずに追従を戻す。
		followBottomRef.current = true;
		setIsNearBottom(true);
	}, []);

	// 追従中はコンテンツ伸長 (ストリーミング/ツール出力/画像ロード/履歴の描き足し) に合わせて最下部へ寄せる。
	// コンテナ側の observe はウィンドウリサイズによる clientHeight 変化を拾うため。
	useEffect(() => {
		const observer = new ResizeObserver(() => {
			if (followBottomRef.current) {
				const el = scrollRef.current;
				if (el) el.scrollTop = el.scrollHeight;
				return;
			}
			updateIsNearBottom();
		});
		if (scrollRef.current) observer.observe(scrollRef.current);
		if (contentRef.current) observer.observe(contentRef.current);
		return () => observer.disconnect();
	}, [updateIsNearBottom]);

	const isLoadingChat = useChatStore((s) => s.isLoadingChat);

	// 別チャットに切り替えた際、読み込み完了後に最新メッセージまでスクロールする。
	useEffect(() => {
		if (isLoadingChat) {
			// 読み込み中は旧チャットの判定を引きずらない (スケルトン上のボタン表示防止)。
			followBottomRef.current = true;
			setIsNearBottom(true);
			return;
		}
		scrollToBottom("auto");
	}, [chatId, isLoadingChat, scrollToBottom]);

	const clearDraft = useChatDraftStore((s) => s.clearDraft);

	const handleSend = useCallback(
		async (text: string, attachments: ComposerAttachment[]) => {
			const initialStore = useChatStore.getState();
			let activeChatId = initialStore.chatId;
			const runSettings = selectMessageRunSettings(initialStore);
			const wasStreamingAtSend = initialStore.status === "streaming";
			if (!selectIsDraftEnvironmentReady(initialStore)) {
				const message = initialStore.isNewChatEnvironmentStatusValidating
					? "エージェントの状態を確認しています。"
					: initialStore.newChatEnvironmentId === null
						? "稼働中のエージェントがありません。"
						: "エージェントのファイルの読み込みが完了するまで送信できません。";
				useToastStore.getState().show({
					type: "error",
					message,
				});
				return;
			}
			// 送信中に表示が別チャットへ切り替わっても、添付は送信した時点の環境へ置く。
			const environmentId = selectEffectiveEnvironmentId(initialStore);
			const ownership = sendOwnershipRef.current.acquire(activeChatId);
			if (ownership === null) return;
			setPendingSendKey(ownership.key, true);
			try {
				// このターンで新規チャットを作成した場合の revision。背後送信 (作成中に別チャットへ
				// 切り替え) ではストアの revision が別チャットを指すため、expectedRevision は
				// 表示状態ではなく送信先チャットの revision を使う。
				let createdChatRevision: number | null = null;
				const isFirstMessage = useChatStore.getState().messages.length === 0;
				// 添付のみの送信ではテキストが空になるため、タイトルが空文字のままにならないようにする。
				const tempChatTitle = text.trim().slice(0, 30) || "添付ファイル";

				// 失敗した送信の本文と添付を入力欄へ戻す。下書き (chatId なし) は戻す先がない。
				const restoreDraft = (restored: ComposerAttachment[]) => {
					if (activeChatId !== null && useChatStore.getState().chatId === activeChatId) {
						useChatDraftStore
							.getState()
							.queueDraftRestore(activeChatId, { text, attachments: restored });
					}
				};

				// 本文に入れるかワークスペースへ置くかは、ここで初めて決める。
				let routed: RoutedAttachments;
				let uploadPlan: ChatAttachmentUploadPlan | null;
				try {
					routed = await routeAttachments(
						attachments.flatMap((a) => (a.kind === "file" ? [a.file] : [])),
					);
				} catch (err) {
					useToastStore.getState().show({
						type: "error",
						message: "添付ファイルを読み込めませんでした。",
					});
					restoreDraft(attachments);
					throw err;
				}
				try {
					uploadPlan = await planChatAttachmentUploads(environmentId, routed.overflow);
				} catch (err) {
					restoreDraft(attachments);
					throw err;
				}
				const uploadedByFile = new Map(
					(uploadPlan?.uploads ?? []).map(({ file, path }) => [
						file,
						uploadedFile(`~/${path}`, file.size),
					]),
				);
				const uploadedFiles: UploadedFile[] = [
					...attachments.flatMap((a) => (a.kind === "uploaded" ? [a.uploaded] : [])),
					...uploadedByFile.values(),
				];
				// アップロード後に送信が失敗したら、置いたファイルは再アップロードせずパスのまま戻す。
				const sentAttachments = attachments.map((a) => {
					const uploaded = a.kind === "file" ? uploadedByFile.get(a.file) : undefined;
					return uploaded ? attachmentFromUploaded(uploaded) : a;
				});

				// Anthropic 推奨に合わせて添付をテキストより先に置く。空文字の text ブロックは
				// API エラーになるため、テキストが無ければブロック自体を省略する。楽観メッセージと
				// POST 本文で同一の content を使い、表示とサーバー保存内容を一致させる。
				const content: ContentBlock[] = [
					...routed.images,
					...routed.documents,
					...uploadedFiles.map(toUploadedFileBlock),
				];
				if (text.length > 0) content.push({ type: "text", text });

				// チャット作成 (下書きの場合) を待たず、まず楽観的にメッセージを表示する。
				// これにより送信フィードバックが即座に返る。clientRequestId は楽観追加の前に
				// 採番して DTO に載せておき、前ターンのストリームがサーバ版を配信した際に
				// 同じ clientRequestId で楽観メッセージへ詰め替えられる (二重表示を防ぐ) ようにする。
				const optimisticId = newMessageId();
				const messages = useChatStore.getState().messages;
				const clientRequestId = newRequestId();
				const userMessage: ChatMessageDto = {
					id: optimisticId,
					chatId: activeChatId ?? "",
					order: nextMessageOrder(messages),
					role: "user",
					content,
					createdAt: new Date().toISOString(),
					createdById: useCurrentUserStore.getState().id,
					stopReason: null,
					stopDetails: null,
					clientRequestId,
				};
				addMessage(userMessage);
				setPendingUserMessage(optimisticId);
				// ユーザー自身の送信では最下部へスクロールする (楽観メッセージの DOM 反映後に実行)。
				requestAnimationFrame(() => scrollToBottom("auto"));

				// 一応、タイトルは最初のメッセージの最初の30文字にする。改名済みならそれを残す。
				if (isFirstMessage && useChatStore.getState().currentTitle === null) {
					const store = useChatStore.getState();
					store.setCurrentTitle(tempChatTitle);
					if (activeChatId) store.updateChatListItem(activeChatId, { title: tempChatTitle });
				}

				// アップロードは時間がかかりうるため、メッセージを表示してから行う。
				if (uploadPlan !== null) {
					try {
						await uploadChatAttachments(uploadPlan);
					} catch (err) {
						const store = useChatStore.getState();
						if (store.chatId === activeChatId && store.pendingUserMessageId === optimisticId) {
							store.removeMessage(optimisticId);
							if (activeChatId === null) store.setCurrentTitle(null);
							restoreDraft(attachments);
						}
						throw err;
					}
				}

				// 新規チャットの下書き状態 (chatId なし) なら、ここで初めてバックエンドに
				// チャットを作成する。
				if (!activeChatId) {
					// 添付を置いた環境と同じ環境に作る。送信中に選択が変わっても読み直さない。
					if (!environmentId) return;
					const environment: NewChatEnvironment = { environmentId };
					let newChat: ChatMetadata;
					try {
						newChat = await createBlankChat(environment);
					} catch (err: unknown) {
						// 作成失敗。まだこの下書きを表示中なら、楽観追加したメッセージを取り消す。
						// 既に別チャットへ切り替えられている場合は、そのチャットの状態に触れない。
						const store = useChatStore.getState();
						if (store.chatId === null && store.pendingUserMessageId === optimisticId) {
							store.removeMessage(optimisticId);
							store.setCurrentTitle(null);
						}
						useToastStore.getState().show({
							type: "error",
							message: "チャットの作成に失敗しました。",
						});
						throw err;
					}
					const draftKey = ownership.key;
					if (!sendOwnershipRef.current.rebind(ownership, newChat.id)) {
						throw new Error("新規チャット送信の所有権を引き継げませんでした。");
					}
					setPendingSendKey(draftKey, false);
					setPendingSendKey(ownership.key, true);
					activeChatId = newChat.id;
					createdChatRevision = newChat.revision;
					const store = useChatStore.getState();
					store.addChatListItem(newChat);
					store.updateChatListItem(newChat.id, { title: tempChatTitle });
					// 作成を待つ間にユーザーが別チャットへ切り替えた / 新しい下書きを始めた場合は
					// 表示を乗っ取らない。メッセージだけがこの新規チャットへ背後で送られる。
					if (store.chatId === null && store.pendingUserMessageId === optimisticId) {
						// メッセージは表示済みなので reset せずメタデータだけを載せる。新規チャットの
						// title は null なので、暫定タイトルで上書きして維持する。
						store.setChatMetadata({ ...newChat, title: tempChatTitle });
						store.setChatRevision(newChat.revision);
						navigate(`/chats/${newChat.id}`, { replace: true });
						// 既存環境を選んで新規チャットを作成した場合、その環境の既存ファイルを
						// すぐツリーへ反映する (作成直後のチャットは必ず環境を持つ)。
						if (newChat.environmentId !== null) {
							refreshEnvironmentFiles(newChat.environmentId);
						}
					}
				}

				// 単一のユーザーメッセージを追記する。POST が 409 を返しうるため、ストリームを
				// 張る前に追記を確定させる (POST 先行)。これでストリームが宙に浮かず、失敗時の
				// ステータスも正しく保てる。
				const expectedRevision = createdChatRevision ?? useChatStore.getState().revision;

				try {
					const result = await postChatMessage(activeChatId, {
						clientRequestId,
						expectedRevision,
						content,
						...runSettings,
					});
					// POST 受理 = ユーザーメッセージは永続化された。表示中なら送信中マークを解除し、
					// 権威的なリビジョンと進行中ジョブ (停止ボタンの対象) を反映する。
					const tookOver = useChatStore.getState().chatId === activeChatId;
					if (tookOver) {
						const store = useChatStore.getState();
						if (result.queued && result.queuedMessage) {
							// アクティブジョブ中の送信はキュー化された。楽観行をタイムラインから外し、
							// 送信待ちエリアへ移す (pendingUserMessageId のクリアも applyQueuedSendResult が担う)。
							store.applyQueuedSendResult(optimisticId, result.queuedMessage);
						} else {
							// 楽観行はクライアント採番の id のままなので、サーバ版 (同じ clientRequestId) で
							// 置き換える。以降この行を id で参照する操作 (会話分岐など) がサーバと一致する。
							const persisted = result.messages.find((m) => m.clientRequestId === clientRequestId);
							if (persisted) store.upsertStreamedMessage(persisted);
							setPendingUserMessage(null);
						}
						store.setChatRevision(result.revision);
						store.setActiveAgentJob(result.agentJobId ?? null);
					}
					clearDraft(activeChatId);

					// 楽観追加したユーザーメッセージが最新の order を占めているため、既定の
					// startFrom (= max(order)) でエージェント応答以降だけがストリームから流れてくる。
					// 表示中でなければ startStreaming は内部ガードで何もしない。
					if (shouldReconnectAfterSend({ queued: result.queued === true, wasStreamingAtSend })) {
						startStreaming(activeChatId);
					}
					if (isFirstMessage) {
						// SSE は server-driven に閉じるため、ここでは完了待ちしない。
						void generateChatTitleAndUpdate(activeChatId).catch((titleError: unknown) => {
							console.error("チャットタイトルの生成に失敗しました", titleError);
						});
					}
				} catch (err: unknown) {
					const store = useChatStore.getState();
					if (store.chatId === activeChatId && store.pendingUserMessageId === optimisticId) {
						// 楽観追加を取り消す。removeMessage が送信中マークも併せて解除する。
						store.removeMessage(optimisticId);
						if (shouldResetStatusToIdleAfterSendFailure(store.status)) store.setStatus("idle");
						restoreDraft(sentAttachments);
					}
					if (err instanceof ChatSendConflictError) {
						// 送信時チェック後に環境が停止していた場合に、次のポーリングを待たず入力欄を閉じる。
						refreshChatEnvironmentStatus();
						if (store.chatId === activeChatId) {
							if (err.revision !== null) store.setChatRevision(err.revision);
							if (!wasStreamingAtSend) {
								try {
									const [messages, activeJob] = await Promise.all([
										listChatMessages(activeChatId),
										getActiveAgentJob(activeChatId),
									]);
									if (useChatStore.getState().chatId === activeChatId) {
										useChatStore.getState().setMessages(messages);
										useChatStore.getState().setActiveAgentJob(activeJob?.id ?? null);
									}
								} catch (resyncErr: unknown) {
									console.error("送信失敗後のチャット再同期に失敗しました", resyncErr);
									useToastStore.getState().show({
										type: "error",
										message: "送信失敗後のチャット再同期に失敗しました。再読み込みしてください。",
									});
								}
							}
						}
						useToastStore.getState().show({ type: "error", message: err.message });
					} else if (err instanceof ChatSendValidationError) {
						useToastStore.getState().show({ type: "error", message: err.message });
					} else {
						useToastStore.getState().show({
							type: "error",
							message: "メッセージの送信に失敗しました。",
						});
					}
					throw err;
				}
			} finally {
				const key = ownership.key;
				sendOwnershipRef.current.release(ownership);
				setPendingSendKey(key, false);
			}
		},
		[
			addMessage,
			setPendingUserMessage,
			clearDraft,
			startStreaming,
			setPendingSendKey,
			navigate,
			scrollToBottom,
		],
	);

	// ネストした子要素をまたぐ dragEnter/dragLeave の対応数を数え、0 に戻ったときだけ
	// オーバーレイを消す (子要素上での一時的な dragLeave で消えてしまうのを防ぐ)。
	const dragDepthRef = useRef(0);
	const [dragActive, setDragActive] = useState(false);

	// dragDepthRef・dragActive を未ドラッグ状態へ戻す。drop 時に加え、ウィンドウ外への
	// ドラッグ離脱やウィンドウのフォーカス喪失時のフォールバックからも呼ばれる。
	const resetDragState = useCallback(() => {
		dragDepthRef.current = 0;
		setDragActive(false);
	}, []);

	const handleDragEnter = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
		if (!isFileDrag(e)) return;
		dragDepthRef.current += 1;
		setDragActive(true);
	}, []);

	const handleDragOver = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
		if (!isFileDrag(e)) return;
		// preventDefault しないと drop が発火しない。
		e.preventDefault();
		e.dataTransfer.dropEffect = "copy";
	}, []);

	const handleDragLeave = useCallback((e: ReactDragEvent<HTMLDivElement>) => {
		if (!isFileDrag(e)) return;
		dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
		if (dragDepthRef.current === 0) setDragActive(false);
	}, []);

	const handleDrop = useCallback(
		(e: ReactDragEvent<HTMLDivElement>) => {
			resetDragState();
			if (!isFileDrag(e)) return;
			e.preventDefault();
			// 添付の振り分けはコンポーザー側に集約する。添付ボタンから選んだ場合と
			// 同じファイルが同じ経路へ進むようにするため、ここでは種別を判定しない。
			composerRef.current?.addFiles(Array.from(e.dataTransfer.files));
		},
		[resetDragState],
	);

	// dragenter/dragleave はブラウザのウィンドウ外へドラッグが抜けたときに発火しないことがあり、
	// その場合 dragDepthRef が 0 に戻らずオーバーレイが残り続ける。relatedTarget が null の
	// dragleave はポインタがドキュメント外へ抜けたことを示すため、ここで確実にリセットする。
	// ドラッグ中に Alt+Tab 等でウィンドウがフォーカスを失った場合の保険として blur でもリセットする。
	useEffect(() => {
		const handleWindowDragLeave = (e: DragEvent) => {
			if (e.relatedTarget === null) resetDragState();
		};
		window.addEventListener("dragleave", handleWindowDragLeave);
		window.addEventListener("blur", resetDragState);
		return () => {
			window.removeEventListener("dragleave", handleWindowDragLeave);
			window.removeEventListener("blur", resetDragState);
		};
	}, [resetDragState]);

	const hitlState = useChatStore((s) => s.hitlState);
	const userChoiceToolUseId = hitlState?.kind === "user-choice" ? hitlState.toolUseId : null;
	const toolApprovalToolUseId = hitlState?.kind === "tool-approval" ? hitlState.toolUseId : null;

	return (
		<div
			data-testid="center-pane"
			style={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				minWidth: 0,
				minHeight: 0,
				overflow: "hidden",
				background: "var(--surface-0)",
			}}
		>
			<ChatHeader chatTitle={currentTitle} onCloseTranscriptSearch={handleCloseTranscriptSearch} />

			{/* ヘッダーを除く履歴+コンポーザー全体をファイルドロップの受付範囲にするラッパー。 */}
			<div
				onDragEnter={handleDragEnter}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
				style={{
					flex: 1,
					minHeight: 0,
					display: "flex",
					flexDirection: "column",
					position: "relative",
				}}
			>
				{dragActive && (
					<div data-testid="chat-drop-overlay" style={DROP_OVERLAY_STYLE}>
						ファイルをドロップして添付
					</div>
				)}

				{/* Transcript — チャット欄の最大幅で中央寄せ */}
				<div
					data-testid="transcript-scroll"
					ref={scrollRef}
					onScroll={handleTranscriptScroll}
					onWheel={handleTranscriptWheel}
					onKeyDown={handleTranscriptKeyDown}
					style={{
						flex: 1,
						minHeight: 0,
						overflowY: "auto",
						scrollbarGutter: "stable",
						display: "flex",
						justifyContent: "center",
					}}
				>
					<div
						ref={contentRef}
						style={{
							width: "100%",
							maxWidth: TRANSCRIPT_MAX_WIDTH,
							padding: `16px ${TRANSCRIPT_PADDING_X}px`,
							display: "flex",
							flexDirection: "column",
							// 既定の align-items:stretch だと、この要素の高さが親 (高さ確定の単一行 flex
							// コンテナ) の高さに固定され、本文が伸びても箱のサイズが変わらない。
							// すると ResizeObserver が発火せず自動追従が働かないため、
							// stretch を外して内容ぶんの高さを持たせる。空チャットの
							// プレースホルダを縦いっぱいに保つため min-height で下限だけ残す。
							alignSelf: "flex-start",
							minHeight: "100%",
						}}
					>
						<ChatView scrollRef={scrollRef} />

						{/* 最新へジャンプ。過去ログ閲覧中のみ、スクロール領域の可視下端に sticky で浮かせる。
						    HITL プロンプトカードはスクロール領域外にあるため衝突しない。
						    スラッシュパレット (zIndex:20) が開いている間は覆い隠される。 */}
						{!isNearBottom && (
							<div style={{ position: "sticky", bottom: 12, height: 0, zIndex: 10 }}>
								<div
									style={{
										position: "absolute",
										bottom: 0,
										left: 0,
										right: 0,
										display: "flex",
										justifyContent: "center",
										pointerEvents: "none",
									}}
								>
									<IconButton
										data-testid="jump-to-latest"
										icon="arrowD"
										size={32}
										iconSize={16}
										background="var(--surface-1)"
										hoverBackground="var(--surface-2)"
										onClick={() => scrollToBottom("smooth")}
										style={{
											pointerEvents: "auto",
											borderRadius: "var(--radius-full)",
											boxShadow: "var(--shadow-card)",
											animation: "izanagi-fade-rise 120ms ease-out",
										}}
									/>
								</div>
							</div>
						)}
					</div>
				</div>

				{userChoiceToolUseId !== null && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<div style={CHAT_COLUMN_STYLE}>
							<UserChoicePrompt key={userChoiceToolUseId} />
						</div>
					</div>
				)}

				{toolApprovalToolUseId !== null && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							flexShrink: 0,
						}}
					>
						<div style={CHAT_COLUMN_STYLE}>
							<ToolApprovalPrompt key={toolApprovalToolUseId} />
						</div>
					</div>
				)}

				{/* Composer — チャット欄の最大幅で中央寄せ。transcript との境界線は引かず、
            ComposerInput 側の shadow-card + shadow-composer-boundary だけでパネルとして浮かせる。
            実行中は送信ボタンが停止ボタンに切り替わる。 */}
				<div
					data-testid="composer-dock"
					style={{
						flexShrink: 0,
						display: "flex",
						justifyContent: "center",
					}}
				>
					<div style={CHAT_COLUMN_STYLE}>
						<ScheduledRestartBanner />
						<ComposerInput
							ref={composerRef}
							key={chatId ?? "no-chat"}
							onSend={handleSend}
							placeholder={
								creditExhausted
									? "組織のクレジット残高が不足しているため、メッセージを送信できません"
									: environmentDeleted
										? "エージェントが削除されたため、このチャットは閲覧専用です"
										: chatArchived
											? "チャットを続けるには、アーカイブを解除してください"
											: environmentStopped
												? "エージェントが起動していないため、メッセージを送信できません"
												: undefined
							}
							inputDisabled={chatArchived || environmentDeleted || environmentStopped}
							sendDisabled={!canComposeMessage || !isDraftEnvironmentReady || creditExhausted}
							sendPending={pendingSendKeys.has(chatId)}
							status={status}
							onStop={handleStop}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
